import { Children, isValidElement, useLayoutEffect, type ReactNode } from 'react';
import { useHorizontalScrollFade } from '@hooks/useHorizontalScrollFade';
import { cn } from '@lib/utils';

/**
 * The width rule (recap/pill redesign §0): with more than one item each takes
 * 90% of the row so the next one peeks — the peek IS the swipe affordance. The
 * 10% non-item remainder is exactly the peek zone the shared scroll-fade mask
 * covers (`useHorizontalScrollFade` uses the matching 10% inset).
 */
const MULTI_ITEM_CLASSES = 'w-[90%] min-w-0 shrink-0 snap-start';
const SINGLE_ITEM_CLASSES = 'w-full min-w-0';

export interface SwipeRowProps {
  /**
   * The row's items, one element each. Conditional children (`{cond && <X/>}`)
   * are counted after React drops them; pass a flat list (a Fragment child
   * counts as ONE item).
   */
  children: ReactNode;
  /**
   * Re-measure trigger forwarded to `useHorizontalScrollFade` (e.g. an id the
   * fade should re-measure on). Defaults to the child count, which already
   * covers items appearing/leaving.
   */
  depKey?: unknown;
  /**
   * The row's scroll-start trigger. Defaults to the first child's React key
   * (front-slot identity). Pass your own when that default is wrong for the
   * surface — because the child keys embed volatile state like phase, or
   * because the row re-orders on its own clock and only SOME reorders should
   * count as a new row start (the live surfaces pass `useLiveFrontResetKey`).
   * A primitive on purpose: a fresh object/array passed here would compare
   * unequal every render and reset the scroll position on every render too.
   */
  startKey?: string | number | null;
  /** Container extras — per-surface spacing (`mb-2`, `gap-3`, `-mx-4 px-4`, …). */
  className?: string;
  /**
   * Accessible name for the row; adds `role="group"`. Omit when the surface
   * labels it. Prefer `ariaLabelledBy` when a VISIBLE section label exists, so
   * the group's name is that same on-screen text (no duplicate hidden name).
   */
  ariaLabel?: string;
  /**
   * Id of a visible element that names the row (a section label above it). Adds
   * `role="group"` and wires `aria-labelledby`, so assistive tech announces the
   * one on-screen name. Takes precedence over `ariaLabel` for the group's name.
   */
  ariaLabelledBy?: string;
}

/**
 * The house swipe row (recap/pill redesign §0) — THE default layout for every
 * pill row and recap-card row. Wraps `useHorizontalScrollFade`'s scroll-fade-x
 * mask idiom (SpacesCarousel / FilterChips) with `snap-x` scrolling and the
 * width rule: >1 child → 90%-wide items with the next peeking; exactly 1 →
 * full width with NO scroll chrome (no overflow container, no snap, no mask).
 * Zero children → renders nothing. Also asserts the row's SCROLL START: on
 * mount, and whenever the row's start trigger changes, `scrollLeft` resets to 0
 * (front-slot identity by default; overridable per surface — see `startKey` and
 * the effect below) — a browser-preserved `scrollLeft` from a previous browse
 * session must never leave a resumed PWA's freshly-reordered first pill
 * scrolled out of view.
 *
 * Archive lists stay vertical (§0) — don't reach for this on the Events-tab
 * past section or Calendar timeline.
 */
export function SwipeRow({
  children,
  depKey,
  startKey,
  className,
  ariaLabel,
  ariaLabelledBy,
}: SwipeRowProps) {
  // toArray drops null/false/undefined, so conditional children count correctly.
  const items = Children.toArray(children);
  // The first slot's identity: a reorder or a new arrival at the front changes
  // this even when the item COUNT doesn't. This is the DEFAULT scroll-start
  // trigger below, unless the caller overrides it via `startKey`.
  const firstKey = items.length > 0 && isValidElement(items[0]) ? items[0].key : null;
  // Hook order: called unconditionally before the empty early-return. `fadeStyle`
  // is the shared both-sides scroll fade — the same hook + `scroll-fade-x` mask
  // the SpacesCarousel and FilterChips use: the left edge fades once the row is
  // scrolled off its start, the right whenever more remains, and both insets
  // animate via the `@property` transitions the utility declares.
  const { scrollRef, fadeStyle } = useHorizontalScrollFade(depKey ?? items.length);
  // What actually drives the reset below: the caller's `startKey` if given,
  // else `firstKey`. See the effect for why a caller would override it.
  const effectiveStartKey = startKey ?? firstKey;

  // Assert the row's SCROLL START whenever the FRONT slot's identity changes.
  // Root cause this fixes: a resumed PWA's scroll container survives with
  // whatever `scrollLeft` the last browse session left behind, and a live
  // refetch that reorders pills live-first can put the now-most-relevant
  // pill to the LEFT of that preserved offset — the row opens scrolled past
  // it. `scrollLeft = 0` (not `scrollTo`): synchronous and jsdom-safe, and
  // with `snap-x snap-mandatory` position 0 IS the first slot's own snap
  // point, so this never fights the snap physics.
  //
  // The DEFAULT trigger is `firstKey`, the front child's own React key: any
  // change of front-slot identity is a new row start. That is right for simple
  // consumers whose row order is stable and whose keys are the artifact id
  // ALONE (`LiveHomeRecap`, `EventsJustEndedStrip`, the recap strips). Later-item
  // appends and removals behind the front leave `effectiveStartKey` untouched,
  // so a mid-browse user scrolled further in is never yanked back by an
  // unrelated arrival.
  //
  // The LIVE surfaces override it, because for them front-identity alone is
  // both too eager and too blunt (PR #1040 review). `LiveSpaceSlot` keys its
  // cells `${event.id}:${phase}` so a phase flip remounts the cell and re-fires
  // its impression — that flip must not read as a new row start. And on every
  // live row the ORDER itself moves on a background clock tick, as events cross
  // phase boundaries and re-rank, so a front-identity trigger reset the scroll
  // under a reader's thumb with no user action. `LiveSpaceSlot` and
  // `LivePillRow` therefore both pass a `useLiveFrontResetKey` token: post-mount,
  // the reset fires ONLY on an observed not-live→live transition at the front —
  // see that hook for the canonical statement of the rule and its case table.
  // NOTE for anyone debugging "a room went live and the row didn't jump": that
  // can be correct. The rule is deliberately conservative about ids this surface
  // never saw as not-live, and it never fires on live-block churn. Check the
  // hook's docblock before changing anything here. Mount still asserts scroll 0
  // for every consumer regardless of the token's value.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.scrollLeft = 0;
    }
    // `scrollRef` is a `useRef` object — referentially stable across renders
    // (never changes identity) — so listing it here can't cause spurious
    // refires; only `effectiveStartKey` changing does.
  }, [effectiveStartKey, scrollRef]);

  if (items.length === 0) {
    return null;
  }

  const multi = items.length > 1;

  // A visible label (aria-labelledby) or a hidden one (aria-label) both name the
  // group; labelledby wins when both are set, so the hidden name is suppressed.
  const labelled = ariaLabel != null || ariaLabelledBy != null;

  return (
    <div
      ref={scrollRef}
      role={labelled ? 'group' : undefined}
      aria-label={ariaLabelledBy ? undefined : ariaLabel}
      aria-labelledby={ariaLabelledBy}
      className={cn(
        'flex gap-2',
        multi && 'snap-x snap-mandatory overflow-x-auto no-scrollbar scroll-fade-x',
        className,
      )}
      style={multi ? { WebkitOverflowScrolling: 'touch', ...fadeStyle } : undefined}
    >
      {items.map((item, index) => (
        <div
          // toArray guarantees keys on elements (caller keys survive, prefixed).
          key={isValidElement(item) ? item.key : index}
          className={multi ? MULTI_ITEM_CLASSES : SINGLE_ITEM_CLASSES}
        >
          {item}
        </div>
      ))}
    </div>
  );
}
